import { z } from 'zod';
export const AUDIENCE_SCORE_COMPONENT_KEYS = [
    'market_size',
    'contact_completeness',
    'organic_demand',
    'campaign_history',
    'scraper_quality',
    'service_fit',
];
/** Vaste gewichten — geen LLM; zie filter-capability-matrix § opportunity. */
export const DEFAULT_AUDIENCE_SCORE_WEIGHTS = {
    market_size: 0.15,
    contact_completeness: 0.25,
    organic_demand: 0.15,
    campaign_history: 0.15,
    scraper_quality: 0.15,
    service_fit: 0.15,
};
export const audienceScoreComponentSchema = z.object({
    key: z.enum(AUDIENCE_SCORE_COMPONENT_KEYS),
    score: z.number().min(0).max(100),
    weight: z.number().min(0).max(1),
    available: z.boolean(),
    detail: z.string().optional(),
});
export const audienceScoreResultSchema = z.object({
    opportunity_score: z.number().min(0).max(100),
    confidence_score: z.number().min(0).max(100),
    components: z.array(audienceScoreComponentSchema),
});
function clamp100(n) {
    return Math.max(0, Math.min(100, n));
}
function scoreMarketSize(leadCount) {
    if (leadCount <= 0)
        return 0;
    return clamp100((leadCount / 200) * 100);
}
function scoreContactCompleteness(c) {
    const avg = (c.with_email_pct + c.with_phone_pct + c.with_account_pct + c.verified_email_pct) / 4;
    return clamp100(avg);
}
function scoreOrganicDemand(gscClicks, ga4Sessions) {
    if (gscClicks == null && ga4Sessions == null)
        return null;
    const gscPart = gscClicks != null ? clamp100((gscClicks / 50) * 100) : null;
    const ga4Part = ga4Sessions != null ? clamp100((ga4Sessions / 500) * 100) : null;
    if (gscPart != null && ga4Part != null)
        return clamp100(gscPart * 0.6 + ga4Part * 0.4);
    return gscPart ?? ga4Part ?? null;
}
function scoreCampaignHistory(c) {
    if (c.count === 0)
        return 20;
    let base = clamp100(c.count * 35);
    if (c.enrollments > 0) {
        const outcomeRate = c.positive_outcomes / c.enrollments;
        base = clamp100(base * 0.4 + outcomeRate * 100 * 0.6);
    }
    return base;
}
function scoreScraperQuality(scraper) {
    if (scraper.discovered <= 0)
        return null;
    const approveRate = scraper.approved / scraper.discovered;
    return clamp100(approveRate * 50 + scraper.high_confidence_ratio * 50);
}
function scoreServiceFit(status) {
    if (status === 'active')
        return 75;
    if (status === 'draft')
        return 50;
    return 35;
}
export function computeAudienceScoreFromInputs(inputs, weights = DEFAULT_AUDIENCE_SCORE_WEIGHTS) {
    const components = [];
    const push = (key, score, detail) => {
        const w = weights[key];
        if (score == null) {
            components.push({ key, score: 0, weight: w, available: false, detail });
            return;
        }
        components.push({ key, score: clamp100(score), weight: w, available: true, detail });
    };
    push('market_size', scoreMarketSize(inputs.lead_count), `${inputs.lead_count} contacten · ${inputs.account_count} bedrijven`);
    push('contact_completeness', scoreContactCompleteness(inputs.completeness), `E-mail ${inputs.completeness.with_email_pct.toFixed(0)}% · account ${inputs.completeness.with_account_pct.toFixed(0)}%`);
    const organic = scoreOrganicDemand(inputs.gsc_clicks_30d, inputs.ga4_sessions_30d);
    push('organic_demand', organic, organic != null
        ? `GSC ${inputs.gsc_clicks_30d ?? 0} clicks · GA4 ${inputs.ga4_sessions_30d ?? 0} sessions (30d)`
        : 'Geen GA4/GSC-snapshots');
    push('campaign_history', scoreCampaignHistory(inputs.campaigns), `${inputs.campaigns.count} campagnes · ${inputs.campaigns.enrollments} enrollments`);
    const scraperScore = inputs.scraper ? scoreScraperQuality(inputs.scraper) : null;
    push('scraper_quality', scraperScore, inputs.scraper
        ? `${inputs.scraper.approved}/${inputs.scraper.discovered} goedgekeurd`
        : 'Geen scraper-kandidaten');
    push('service_fit', scoreServiceFit(inputs.status), `Status ${inputs.status}`);
    const available = components.filter((c) => c.available);
    const weightSum = available.reduce((s, c) => s + c.weight, 0);
    const opportunity = weightSum > 0
        ? available.reduce((s, c) => s + c.score * (c.weight / weightSum), 0)
        : 0;
    let confidence = (available.length / AUDIENCE_SCORE_COMPONENT_KEYS.length) * 100;
    if (inputs.lead_count >= 10)
        confidence += 10;
    if (inputs.gsc_clicks_30d != null || inputs.ga4_sessions_30d != null)
        confidence += 10;
    if (inputs.lead_count < 5)
        confidence = Math.min(confidence, 55);
    confidence = clamp100(confidence);
    return audienceScoreResultSchema.parse({
        opportunity_score: clamp100(Math.round(opportunity * 10) / 10),
        confidence_score: clamp100(Math.round(confidence * 10) / 10),
        components,
    });
}
//# sourceMappingURL=computeAudienceScore.js.map